import { EventBus } from '../../../../shared/EventBus';
import { playerCombatPoint } from '../../../../shared/logic/CombatAnchors';
import { serverOwnsAreaDamage } from '../../../../shared/combat/serverAreaAuthority';
import { ServiceRegistry } from '../../../core/ServiceRegistry';
import { casterAuraPlan } from '../../../effects/castGeometry/castVisualPlan';
import { emitVisualPlan } from '../../../effects/castGeometry/emitVisualPlan';
import { SequencedCastWindup } from '../../combat/SequencedCastWindup';
import type { CasterAuraConfig } from './AbilityBuilderFactory';
import type { AbilityMetadata } from './AbilityRegistry';
import type { AbilityBehavior } from './behaviors/types';
import type { ActionContext } from './types';

interface Options {
  abilityId: string;
  metadata: Partial<AbilityMetadata>;
  behaviors: AbilityBehavior[];
  windUpDuration: number;
  windUpVFX: string | null;
  hasProjectile: boolean;
  casterAuraConfig: CasterAuraConfig | null;
}

/** Non-projectile behavior composition. Existing abilities retain their delay;
 * server-owned Howl additionally owns its tell and completion as one lifetime. */
export function createAbilityWindup(options: Options): (ctx: ActionContext) => void {
  const { abilityId, metadata, behaviors, windUpDuration, windUpVFX, hasProjectile, casterAuraConfig } = options;
  const owned = serverOwnsAreaDamage(abilityId) ? new SequencedCastWindup() : null;
  return ctx => {
    const runAll = () => {
      if (casterAuraConfig) emitVisualPlan(casterAuraPlan(casterAuraConfig, abilityId, ctx.currentPlayer.id));
      for (const behavior of behaviors) behavior.execute(ctx, metadata as AbilityMetadata);
    };
    if (windUpDuration <= 0 || !windUpVFX || hasProjectile) { runAll(); return; }
    const player = ctx.currentPlayer;
    const tell = (instanceId?: string) => EventBus.getInstance().emit('vfx:spawn', {
      effectId: windUpVFX, ...playerCombatPoint(player), duration: windUpDuration,
      casterId: player.id, abilityId, ...(instanceId ? { instanceId } : {}),
    });
    if (!owned) { tell(); setTimeout(runAll, windUpDuration); return; }
    const registry = ServiceRegistry.getInstance();
    const manager = registry.get('EntitySystem', { optional: true })?.getPlayerManager?.();
    const room = registry.get('NetworkManager', { optional: true })?.getRoom?.();
    const schema = room?.state?.players?.get?.(player.id);
    owned.start({ casterId: player.id, abilityId, clientCastSeq: ctx.clientCastSeq,
      duration: windUpDuration, ignorePredictedOverwrite: true, tell, complete: runAll,
      isCurrent: () => !(player as { isDead?: boolean }).isDead && (player.health === undefined || player.health > 0) && player.isOnline !== false
        && (!manager?.getPlayer || manager.getPlayer() === player)
        && (!room || (registry.get('NetworkManager', { optional: true })?.getRoom?.() === room
          && room.state.players.get(player.id) === schema && !!schema && !schema.isDead && schema.health > 0 && schema.isOnline !== false)),
    });
  };
}
